import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { DecisionMakerProfilePageCombinedState, DecisionMakerProfilePagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { DecisionMakerProfilePageDataContextProvider } from './pageData';
import { DMProfilePagePaginatedResponsesDataContextProvider } from './pageResponsesPaginatedData';
import { DMProfilePagePaginatedTargetedPetitionsDataContextProvider } from './pageTargetedPetitionsPaginatedData';

export type ErrorHandlerProps = { context: ErrorStateDetails<DecisionMakerProfilePageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: DecisionMakerProfilePagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function DecisionMakerProfilePageContextProvider({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(slug, prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, responsesPaginatedData, targetedPetitionsPaginatedData } = pageContextState;

	return (
		<DecisionMakerProfilePageDataContextProvider pageData={pageData}>
			<DMProfilePagePaginatedResponsesDataContextProvider responsesPaginatedData={responsesPaginatedData}>
				<DMProfilePagePaginatedTargetedPetitionsDataContextProvider
					targetedPetitionsPaginatedData={targetedPetitionsPaginatedData}
				>
					<TrackingContext properties={{ decision_maker_id: pageData.data.id }}>{children}</TrackingContext>
				</DMProfilePagePaginatedTargetedPetitionsDataContextProvider>
			</DMProfilePagePaginatedResponsesDataContextProvider>
		</DecisionMakerProfilePageDataContextProvider>
	);
}
